import React from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Transition } from '@headlessui/react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '../../i18n/config';
import { useLanguagePreference } from '../../hooks/useLanguagePreference';
import { Fragment } from 'react';
import { toast } from 'react-hot-toast';

export interface LanguageSwitcherProps {
  className?: string;
  showLabel?: boolean;
  compact?: boolean; // icon-only mode (globe icon, no border/flag)
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className = '',
  showLabel = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const { currentLanguage, updateLanguagePreference, isUpdating } = useLanguagePreference();

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    if (lang === currentLanguage || isUpdating) return;
    
    try {
      await updateLanguagePreference(lang);
      // Success message
      const langName = SUPPORTED_LANGUAGES[lang].nativeName;
      toast.success(t('common:languageChanged', { language: langName }));
    } catch (error) {
      console.error('Failed to update language preference:', error);
      // User-friendly error message
      toast.error(t('common:languageChangeFailed'));
    }
  };

  const currentLangInfo = SUPPORTED_LANGUAGES[currentLanguage as SupportedLanguage] || SUPPORTED_LANGUAGES.en;

  return (
    <Menu as="div" className={`relative inline-block text-left ${className}`}>
      <div>
        <Menu.Button
          className={compact
            ? "p-2 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            : "inline-flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          }
          disabled={isUpdating}
        >
          <GlobeAltIcon className={compact ? "w-6 h-6" : "w-5 h-5 mr-2"} aria-hidden="true" />
          {!compact && <span className="mr-1">{currentLangInfo.flag}</span>}
          {!compact && showLabel && (
            <span className="ml-1">{currentLangInfo.nativeName}</span>
          )}
          {isUpdating && (
            <svg
              className="w-4 h-4 ml-2 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 w-56 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, info]) => {
              const isActive = code === currentLanguage;
              return (
                <Menu.Item key={code}>
                  {({ active }) => (
                    <button
                      onClick={() => handleLanguageChange(code as SupportedLanguage)}
                      className={`${
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                      } ${
                        isActive ? 'font-semibold' : ''
                      } group flex w-full items-center px-4 py-2 text-sm`}
                      disabled={isUpdating}
                    >
                      <span className="mr-3 text-xl">{info.flag}</span>
                      <span className="flex-1 text-left">{info.nativeName}</span>
                      {isActive && (
                        <svg
                          className="w-5 h-5 text-indigo-600"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
